<script>
	import { project_list, show_sidebar, logged_in } from '../app-store';
    import { navigate } from 'svelte-routing';
    import { onDestroy, onMount, tick } from 'svelte';

    let sidebar_el = null;

    export const scrollToProject = (project_ref) => setTimeout(() => {
        if (!project_ref) return;
        const projectElement = document.getElementById(project_ref);
        if (!projectElement) return;
        sidebar_el.scrollTo({
            top: projectElement.offsetTop - 130, // Sidebar header height
            behavior: 'smooth'
        });
    });

    function onProjectClick (project_ref) {
        project_list.update((projects) => {
            projects.forEach((p) => {
                if (p.project_ref === project_ref) {
                    p.is_active = !p.is_active;
                } else {
                    p.is_active = false;
                }
            });
            return projects;
        });
        scrollToProject(project_ref);
    };

    function onDraftClick(project_ref, draft_refcode) {

        project_list.update((projects) => {
            projects.forEach((p) => {

                if (p.project_ref != project_ref) {
                    p.active = false;
                    p.drafts.forEach(d => d.is_active = false);
                    p.drafts = [...p.drafts];
                    return;
                }

                p.active = true;
                p.drafts.forEach(d => d.is_active = d.refcode == draft_refcode);
                p.drafts = [...p.drafts];
            });
            return projects;
        });
        // On mobile devices, close the sidebar when navigating to a new draft since it overlays the entire page
        if (window.innerWidth < 992) {
            show_sidebar.set(false);
        }
        navigate(`/new-draft/${project_ref}/${draft_refcode}`);
    }

    function onDraftRename(project, draft) {
        const max_length = 1000;
        const rename = async (project_ref, refcode) => {
            const btn_draft_rename = document.getElementById("btn-draft-rename");
            const draftname = document.getElementById("draft-name").value;
            if (!draftname) {
                window.app.toast_msg.show("Draft name cannot be empty");
                return;
            }
            if (draftname.length > max_length) {
                window.app.toast_msg.show(`Draft name cannot be more than ${max_length} characters`);
                return;
            }
            try {
                btn_draft_rename.classList.add("disabled");
                btn_draft_rename.textContent = "Renaming...";
                const { status, title } = await window.app.lucep_ai.rename_draft(refcode, draftname);

                if (status === "ok") {
                    window.app.toast_msg.show("Draft renamed successfully");
                    window.app.modal.$set({ show: false });

                    project_list.update((projects) => {
                        projects.forEach((p) => {
                            if (p.project_ref != project_ref) return;

                            p.drafts.forEach(d => {
                                if (d.refcode == refcode)
                                    d.draftname = title;
                            });
                            p.drafts = [...p.drafts];
                        });
                        return projects;
                    });

                } else {
                    window.app.toast_msg.show("Error renaming draft");
                }
            } catch (error) {
                console.error("Error renaming draft:", error);
                window.app.toast_msg.show("Error renaming draft: " + error.msg || "");
            } finally {
                window.app.modal.$set({ show: false });
                btn_draft_rename.classList.remove("disabled");
                btn_draft_rename.textContent = "Rename";
            }
        };
        window.app.modal.$set({
            show: true,
            title: "Rename Draft",
            modalBodyEl: tag.div(
                { class: "modal-rename" },
                tag.input({
                    type: "text",
                    class: "field-input m-0",
                    id: "draft-name",
                    value: draft.draftname,
                })
            ),
            modalFooterEl: tag.button({id: "btn-draft-rename", onclick: () => rename(project.project_ref, draft.refcode), class: "btn btn-primary" }, "Rename"),
        });
    }

    function onAddButtonClick() {
        navigate(`/project-list`);
    }

    let unsub_proj = project_list.subscribe((projects) => {
        const selectedProject = projects.find((p) => p.is_active);
        if (selectedProject) {
            console.log("Selected project:", selectedProject);
            scrollToProject(selectedProject.project_ref);
        } else {
            console.log("No project selected");
        }
    });

    function setSidebarDefaultState() {
        if (window.innerWidth < 992) {
            show_sidebar.set(false);
        } else {
            if ($logged_in && !$show_sidebar) {
                show_sidebar.set(true);
            } 
        }
    }

    const unsubLoggedIn = logged_in.subscribe((value) => {
        if (value) {
            show_sidebar.set(true);
        } else {
            show_sidebar.set(false);
        }
    });

    onMount(() => {
        setSidebarDefaultState();
        window.addEventListener('resize', setSidebarDefaultState);
    });

    onDestroy(() => {
        unsub_proj();
        window.removeEventListener('resize', setSidebarDefaultState);
        unsubLoggedIn();
    });

</script>

<aside bind:this={sidebar_el} class="sidebar {$show_sidebar ? 'open' : ''}" id="sidebar-history">
    <ul class="sidebar-group" id="sidebar-header">
        <li class="sidebar-item" id="logo">
            <img src="/img/lucep-ai-logo.webp" alt="Logo" class="logo" width="100" />
        </li>
        <li class="sidebar-item" id="logo-options-divider"><hr class="sidebar-divider" /></li>
    </ul>
    <ul class="sidebar-group" id="project-group">
        {#each $project_list as project}
            <li class="sidebar-item">
                <div class="project-item">
                    <button type="button" on:click={() => onProjectClick(project.project_ref)} id={project.project_ref} class="project-item-wrapper btn btn-light {project.is_active ? '' : 'fold'}">
                        <img src="/img/folders-line.svg" width="16" height="16" class="icon" alt="project folder" />
                        <div class="project-item-name">{project.name}</div>
                    </button>
                    <div class="sidebar-items animate-in" id={project.projectref + "-drafts"}>
                        {#if project.drafts && project.drafts.length == 0}
                            <small class="drafts-header mx-3 text-muted">No Drafts</small>
                        {/if}
                        {#each project.drafts as draft}
                            <div class="draft-item" role="button">
                                <button type="button" on:click={() => onDraftClick(project.project_ref, draft.refcode)} class="btn btn-light btn-draft-item rounded-3 {draft.is_active ? 'active' : ''}">
                                    <img src="/img/file-text-line.svg" width="16" height="16" alt="draft file" />
                                    <div class="draft-item-draftname">
                                        {draft.draftname || "Untitled Draft"}
                                    </div>
                                </button>
                                <div class="btn-group draft-options">
                                    <button
                                        class="btn btn-light btn-draft-options-expand dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                    ></button>
                                    <ul class="dropdown-menu p-3">
                                        <li class="dropdown-header small text-muted">
                                            <ul class="list-inline">
                                                <li class="text-wrap mb-1">{draft.draftname || "Untitled Draft"}</li>
                                                <li class="mb-1">{draft.email}</li>
                                                <li>{new Date(draft.createdwhen * 1000).toLocaleString()}</li>
                                            </ul>
                                        </li>
                                        <li class="dropdown-divider"></li>
                                        <li class="dropdown-item p-0">
                                            <button type="button" on:click={() => onDraftRename(project, draft)} class="btn btn-light w-100 rounded-3">Rename</button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            </li>
        {/each}
    </ul>
</aside>


<style>
    /* Sidebar */
    .sidebar {
        grid-column: 1;
        grid-row: span 2;
        width: 100%;
        height: 100%;
        position: fixed;
        overflow-x: hidden;
        overflow-y: auto;
        background-color: #f8ecde;
        transform: translateX(-100%);
        transition: 0.35s ease all;
        z-index: 200;
        top: 80px;
        padding-bottom: 80px;
    }
    .sidebar.open {
        transform: translateX(0);
    }
    .sidebar-group {
        padding: 0;
        margin: 0;
    }
    .sidebar .sidebar-options .btn {
        background-color: transparent;
        border: none;
        box-shadow: none;
        margin-top: 0;
        color: var(--text-muted-color-6);
        display: flex;
        justify-content: center;
        border-radius: 10px;
    }
    .sidebar .sidebar-options .btn:not(:disabled):hover {
        background-color: #7f5700;
    }
    .sidebar-item {
        list-style-type: none;
        margin-bottom: 1rem;
    }
    .sidebar-item .project-item-wrapper {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem;
        cursor: pointer;
        border-radius: 0.25rem;
    }
    .sidebar-item .project-item-wrapper.fold + .sidebar-items {
        display: none;
    }
    .sidebar-item .project-item-wrapper:hover {
        background-color: var(--text-muted-color-c);
    }
    .sidebar-item .project-item-wrapper:not(.fold) {
        background-color: var(--text-muted-color-c);
        border-radius: 0;
        margin-bottom: 1rem;
    }
    .sidebar-item .project-item .project-item-name {
        margin: 0;
        font-size: 0.85rem;
        font-weight: bold;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
    .sidebar-item .draft-item {
        display: flex;
        gap: 0.1rem;
        margin-left: 15px;
        border-radius: 20px;
        margin-bottom: 10px;
    }
    .sidebar-item .btn-draft-item.active {
        background-color: #7f5700;
        color: #fff;
    }
    .sidebar-item .btn-draft-item.active:hover {
        background-color: #a87300;
    }
    .draft-item-draftname {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .sidebar .btn {
        background-color: #f8ecde;
        color: var(--prione);
        font-size: 0.85rem;
        box-shadow: none;
        border: none;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        padding: 7px 7px;
        gap: 5px;
    }
    .sidebar .btn:not(.active):hover {
        background-color: #dadbd2;
    }
    .sidebar .group-item.active .btn {
        text-align: left;
        background-color: var(--prione);
    }
    #sidebar-header {
        position: sticky;
        top: 0;
        background: #f8ecde;
        z-index: 10;
    }
    .sidebar-item#logo {
        padding-bottom: 1.5rem;
        padding-top: 1.5rem;
    }
    .sidebar::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }
    .sidebar::-webkit-scrollbar {
        width: 0.5rem;
    }
    .sidebar::-webkit-scrollbar-thumb {
        background-color: var(--text-muted-color-c);
        border-radius: 30px;
    }
    @media screen and (min-width: 992px) {
        .sidebar {
            width: 250px;
        }
    }
</style>